import { ABTestEnv } from '@meitu/ab-test-core';
import { ABTest } from '@meitu/ab-test';
import { getUrlParam, handleMergeAbinfo } from '.';
import * as Sentry from '@sentry/react';
import {
  isTeamSubscription,
  isFromLivePPT,
  SubscribeType,
  subscribeType,
  isFromAiLogo,
  isFromPcHome,
  isFromCutout,
  isFromAiPostgen,
  isFromAiCopywriting,
  isFromAiHb,
} from '@/constants';
import { getIncomeSource } from './vip';
import { TabType } from '@/constants/subscription';
import { curTabAtom } from '@/store';
import { getDefaultStore } from 'jotai';

const store = getDefaultStore();

let abTest: any;

/**
 * 初始化ab实验
 */
export const initAbTest = async () => {
  try {
    // 实例化 ABTest SDK
    const abTestRef = new ABTest({
      appKey: isFromCutout ? '11CB34B59399E101' : 'C5F34252F04B6D50', // 抠图Meepo实验11CB34B59399E101，美图设计室Meepo实验C5F34252F04B6D50
      env: ABTestEnv.Release, // release|beta, beta就是测试环境
    });

    await abTestRef.init(); // 初始化 ABTest SDK

    abTest = abTestRef;

    // 通过 WebSDK 设置 ab_info 的信息
    const localAbInfo = abTest.getABInfo();
    let abInfo = handleMergeAbinfo(localAbInfo);
    window.mtstat?.setABInfo(abInfo);
  } catch (e) {
    console.log('initAbTest error==========', e);
    Sentry.captureException(`ABTest error >>>>>>>: ${e}`);
  }
};

/**
 * 获取合并后的最终abInfo
 */
export const getAbInfo = () => {
  return handleMergeAbinfo(abTest?.getABInfo() || {});
};

/**
 * 是否进入美豆实验组
 */
export const checkIsInMeidouExperimentalGroup = () => {
  // 美豆场景，且web端，且非团队版时才做实验
  if (
    !(
      !isTeamSubscription &&
      subscribeType === SubscribeType.MEIDOU &&
      getIncomeSource() === 'MTXX-WEB'
    )
  ) {
    return false;
  }

  // 对照组
  if (abTest?.isInABTesting(26227)) {
    return false;
  }

  // 实验组
  if (abTest?.isInABTesting(26228)) {
    return true;
  }

  return false;
};

/**
 * 是否进入会员实验组
 */
export const checkIsInSubExperimentalGroup = () => {
  // 团队版/PC内嵌，不做实验
  if (isTeamSubscription || getIncomeSource() !== 'MTXX-WEB') {
    return false;
  }

  // PPT场景
  if (isFromLivePPT || isFromAiLogo || isFromPcHome || isFromCutout) {
    return false;
  }

  // 对照组
  if (abTest?.isInABTesting(27498)) {
    return false;
  }

  // 实验组
  if (abTest?.isInABTesting(27499)) {
    return true;
  }

  return false;
};

/**
 * 是否进入PPT实验组
 */
export const checkIsInPPTExperimentalGroup = () => {
  if (
    (!isFromLivePPT && !isFromAiLogo) ||
    isTeamSubscription || // 团队版，不做实验
    getIncomeSource() !== 'MTXX-WEB' || // PC内嵌
    abTest?.isInABTesting(27220) // 对照组
  ) {
    return {
      isHitGroupOne: false,
      isHitGroupTwo: false,
    };
  }

  // 实验组1
  if (abTest?.isInABTesting(27221)) {
    return {
      isHitGroupOne: true,
      isHitGroupTwo: false,
    };
  }

  // 实验组2
  if (abTest?.isInABTesting(27222)) {
    return {
      isHitGroupOne: false,
      isHitGroupTwo: true,
    };
  }

  return {
    isHitGroupOne: false,
    isHitGroupTwo: false,
  };
};

/**
 * 是否进入抠图实验组
 */
export const checkIsInCutoutExperimentalGroup = () => {
  if (
    !isFromCutout ||
    isTeamSubscription || // 团队版，不做实验
    getIncomeSource() !== 'MTXX-WEB' || // PC内嵌
    abTest?.isInABTesting(27217) // 对照组
  ) {
    return {
      isHitGroupOne: false,
      isHitGroupTwo: false,
    };
  }

  // 实验组1
  if (abTest?.isInABTesting(27218)) {
    return {
      isHitGroupOne: true,
      isHitGroupTwo: false,
    };
  }

  // 实验组2
  if (abTest?.isInABTesting(27219)) {
    return {
      isHitGroupOne: false,
      isHitGroupTwo: true,
    };
  }

  return {
    isHitGroupOne: false,
    isHitGroupTwo: false,
  };
};

/**
 * 是否进入会员实验组（思凯需求）
 * https://cf.meitu.com/confluence/pages/viewpage.action?pageId=632704539&focusedCommentId=643440856&refresh=1764558233607#comment-643440856
 */
export const checkIsInSubExperimentalGroupInfo = () => {
  // 团队版/PC内嵌，不做实验
  // PPT场景，logo，pc站点，ai图文，ai文案，ai海报
  if (
    isTeamSubscription ||
    getIncomeSource() !== 'MTXX-WEB' ||
    isFromLivePPT ||
    isFromAiLogo ||
    isFromPcHome ||
    isFromAiPostgen ||
    isFromAiCopywriting ||
    isFromAiHb
  ) {
    return {
      isHitGroupOne: false,
      isHitGroupTwo: false,
    };
  }

  // 抠图应用和设计室应用
  // 对照组
  if (abTest?.isInABTesting(29468) || abTest?.isInABTesting(29535)) {
    return {
      isHitGroupOne: false,
      isHitGroupTwo: false,
    };
  }

  // 实验组1
  if (abTest?.isInABTesting(29469) || abTest?.isInABTesting(29542)) {
    return {
      isHitGroupOne: true,
      isHitGroupTwo: false,
    };
  }

  // 实验组2
  if (abTest?.isInABTesting(29470) || abTest?.isInABTesting(29543)) {
    return {
      isHitGroupOne: false,
      isHitGroupTwo: true,
    };
  }

  return {
    isHitGroupOne: false,
    isHitGroupTwo: false,
  };
};

/**
 * 高级会员默认美豆第一档还是第二档实验
 */
export const checkIsDefaultSecondTierMeidou = () => {
  // 团队版/PC内嵌，不做实验
  // 高级会员tab下
  const curTab = store.get(curTabAtom);

  if (isTeamSubscription || getIncomeSource() !== 'MTXX-WEB' || curTab !== TabType.PRO_SUB) {
    return false;
  }

  // 实验组
  if (abTest?.isInABTesting(30821) || abTest?.isInABTesting(30826)) {
    return true;
  }

  // 抠图应用和设计室应用
  // 对照组
  if (abTest?.isInABTesting(30820) || abTest?.isInABTesting(30825)) {
    return false;
  }

  return false;
};
